import "@shoelace-style/shoelace/dist/components/progress-bar/progress-bar.js";

import { TemplateResult, css, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { Style } from "./Styles";
import { GarminBuilder, StravaBuilder, BaseBuilder, buildGPX } from "gpx-builder";
import { BaseElement } from "./BaseElement";
import { downloadToFile, makeCustomEvent } from "../Utils";
import { renderEnumSelect } from "./form/EnumSelect";
import { PlaceTable } from "../backend/cloud/PlaceTable";

const { Point } = GarminBuilder.MODELS;

enum GPXExportFormats {
    Standard = "Standard",
    Garmin = "Garmin",
    Strava = "Strava",
}

@customElement("ds-gpx-export")
export class GPXExport extends BaseElement {
    static override styles = [
        Style,
        css`
            .container {
                display: flex;
                flex-direction: column;
                width: 100%;
            }
        `,
    ];

    @state() protected _progress: number | undefined = undefined;
    @state() protected _format = GPXExportFormats.Garmin;

    public override async connectedCallback(): Promise<void> {
        super.connectedCallback();
    }

    private async _export() {
        const places = this._kernel.dataStore.getSource<PlaceTable>("places");
        const filter = this._kernel.currentPlaceFilter.get();
        const all = [...places.allFilteredItems(filter).filter((x) => x.data.locationKnown)];
        const dateString = new Date().toString();

        // Garmin explorer is limited to 500 waypoints per import file
        const MAX_WAYPOINTS = this._format === GPXExportFormats.Garmin ? 500 : Number.MAX_VALUE;

        const numExportFiles = Math.ceil(all.length / MAX_WAYPOINTS);
        for (let i = 0; i < numExportFiles; ++i) {
            const points = all
                .slice(i * MAX_WAYPOINTS, i * MAX_WAYPOINTS + MAX_WAYPOINTS)
                .map((x, index) => {
                    this._progress = (i * MAX_WAYPOINTS + index) / all.length;
                    return new Point(x.data.location.lat, x.data.location.lon, {
                        name: `${x.id.slice(0, 5)} ${x.data.name}`,
                        cmt: x.id,
                        src: "cavedb.net",
                        desc: x.id,
                    });
                });

            const gpxData =
                this._format === GPXExportFormats.Garmin
                    ? new GarminBuilder()
                    : this._format === GPXExportFormats.Strava
                      ? new StravaBuilder()
                      : new BaseBuilder();
            gpxData.setWayPoints(points);
            const xml = buildGPX(gpxData.toObject());
            downloadToFile(
                xml,
                "text/xml",
                `cavedb-${this._format.toString().toLocaleLowerCase()}-gps-${dateString}-${i}.gpx`
            );
        }
        this.dispatchEvent(makeCustomEvent("on-complete"));
    }

    override render(): TemplateResult {
        return html`<div class="container">
            ${renderEnumSelect(this, GPXExportFormats, this, "_format", undefined)}
            <br />
            ${this._progress
                ? html`<sl-progress-bar .value=${this._progress * 100}></sl-progress-bar>`
                : html`<sl-button @click=${() => this._export()}>Export</sl-button>`}
        </div>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-gpx-export": GPXExport;
    }
}
